import React, { useState } from "react";
import { mockEducationEnrollments, mockFigurLabProjects, mockOrders } from "../../content/store/account";
import { useStoreAuth } from "../../contexts/StoreAuthContext";
import { OrderSummary } from "../../types/store";

const tabs = ["Orders", "Education", "Figur Lab", "Profile"] as const;

const AccountPage: React.FC = () => {
    const { user } = useStoreAuth();
    const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>("Orders");

    if (!user) {
        return (
            <div className="rounded-3xl border border-dashed border-white/20 bg-white/5 p-12 text-center text-white/70">
                Please sign in to access the unified Modelerman account area.
            </div>
        );
    }

    const renderOrders = () => {
        if (mockOrders.length === 0) {
            return <p className="text-white/60">You don’t have any purchases yet.</p>;
        }
        return (
            <div className="space-y-4">
                {mockOrders.map((order: OrderSummary) => (
                    <div key={order.id} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <p className="text-sm text-white/60">{order.date}</p>
                                <p className="text-xl font-semibold">{order.id}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-white/60">Status</p>
                                <p className="text-base font-semibold">{order.status}</p>
                                <p className="text-sm text-white/70">${order.total.toFixed(2)}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2 text-sm text-white/70">
                            {order.items.map((item, index) => (
                                <div key={`${order.id}-${index}`} className="flex items-center justify-between text-xs sm:text-sm">
                                    <span>
                                        {item.productName} • {item.productType}
                                        {item.license ? ` • ${item.license}` : ""}
                                    </span>
                                    <span>{item.isDigital ? "Download" : "Track shipment"}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderEducation = () => {
        if (mockEducationEnrollments.length === 0) {
            return <p className="text-white/60">You haven’t joined any programs yet.</p>;
        }
        return (
            <div className="grid gap-4 md:grid-cols-2">
                {mockEducationEnrollments.map((program) => (
                    <div key={program.id} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                        <p className="text-sm text-white/60">Progress {program.progress}%</p>
                        <p className="text-xl font-semibold">{program.name}</p>
                        <div className="mt-4 h-2 rounded-full bg-white/10">
                            <div className="h-full rounded-full bg-accent" style={{ width: `${program.progress}%` }} />
                        </div>
                        <a
                            className="mt-4 inline-flex items-center text-sm font-semibold text-accent"
                            href={program.ctaUrl}
                        >
                            Go to course →
                        </a>
                    </div>
                ))}
            </div>
        );
    };

    const renderFigurLab = () => {
        if (mockFigurLabProjects.length === 0) {
            return <p className="text-white/60">No custom figure projects yet.</p>;
        }
        return (
            <div className="space-y-4">
                {mockFigurLabProjects.map((project) => (
                    <div key={project.id} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-white/50">{project.id}</p>
                                <p className="text-lg font-semibold">{project.prompt}</p>
                            </div>
                            <div className="text-right text-sm">
                                <p className="font-semibold">{project.status.replace(/_/g, " ")}</p>
                                <p className="text-white/60">Updated {project.updatedAt}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderProfile = () => (
        <div className="space-y-4 text-sm text-white/70">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Identity</p>
                <p className="text-lg font-semibold">{user.name ?? "Unnamed"}</p>
                <p>{user.email}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">Roles</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {user.roles.map((role) => (
                        <span key={role} className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-wide">
                            {role}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-10">
            <header>
                <p className="text-xs uppercase tracking-[0.3em] text-white/50">My Account</p>
                <h1 className="text-3xl font-semibold">Unified Library</h1>
                <p className="text-sm text-white/60">Orders, Education, Figur Lab — one history, one login.</p>
            </header>

            <div className="flex flex-wrap gap-3">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`rounded-full px-5 py-2 text-sm ${activeTab === tab ? "bg-white text-black" : "bg-white/5 text-white/70"
                            }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <div>
                {activeTab === "Orders" && renderOrders()}
                {activeTab === "Education" && renderEducation()}
                {activeTab === "Figur Lab" && renderFigurLab()}
                {activeTab === "Profile" && renderProfile()}
            </div>
        </div>
    );
};

export default AccountPage;
